import ApiService from './ApiService';
import Logger from './Logger';
import { getContentLanguage } from './LangPrefs';
import { getAssistantChatHistory, postAssistantHelp } from './AssistantService';
import SlidingService from '../pages/puzzles/sliding/SlidingService';
import JigsawService from '../pages/puzzles/jigsaw/JigsawService';

/**
 * Fetches home page puzzles (1 Crossword, 1 Word Search, 1 Hidden Message) in a single request.
 * @param {string} [language] - Optional language (e.g. 'english', 'french'). Defaults to getContentLanguage().
 * @returns {Promise<Array>} Array of puzzle objects in order: crossword, hiddenMessage, wordSearch
 */
const getHomePuzzles = async (language) => {
    try {
        const lang = language || getContentLanguage();
        const url = `/home/cache/puzzles?language=${encodeURIComponent(lang)}`;
        const data = await ApiService.getData(url);

        if (!data) return [];

        const crossword = data.crossword ? { ...data.crossword, type: 'CROSSWORD' } : null;
        const wordSearch = data.wordSearch ? { ...data.wordSearch, type: 'WORD_SEARCH' } : null;
        const hiddenMessage = data.hiddenMessage ? { ...data.hiddenMessage, type: 'HIDDEN_MESSAGE' } : null;

        return [crossword, hiddenMessage, wordSearch].filter(Boolean);
    } catch (error) {
        Logger.error('Error fetching home puzzles:', error);
        return [];
    }
};

const filterBedrockTags = (list) => {
    const bedrockLower = 'bedrock';
    return list.filter((item) => {
        const tagStr = typeof item === 'string' ? item : (item?.tag ?? item?.name ?? '');
        return !String(tagStr).toLowerCase().includes(bedrockLower);
    });
};

/**
 * Fetches tags for crossword puzzles (home/cache endpoint).
 * @param {string} [language] - Optional language (e.g. 'english', 'french')
 * @returns {Promise<Array>} Array of tag strings or objects
 */
const getCrossTags = async (language) => {
    try {
        const lang = language || getContentLanguage();
        const url = `/home/cache/cross/tags?language=${encodeURIComponent(lang)}&state=PUBLISHED`;
        const data = await ApiService.getData(url);
        const list = Array.isArray(data) ? data : [];
        return filterBedrockTags(list);
    } catch (error) {
        Logger.error('Error fetching cross tags:', error);
        return [];
    }
};

/**
 * Fetches tags for grid puzzles (word search / hidden message) - home/cache endpoint.
 * @param {string} [language] - Optional language (e.g. 'english', 'french')
 * @returns {Promise<Array>} Array of tag strings or objects
 */
const getGridTags = async (language) => {
    try {
        const lang = language || getContentLanguage();
        const url = `/home/cache/grid/tags?language=${encodeURIComponent(lang)}&state=PUBLISHED`;
        const data = await ApiService.getData(url);
        const list = Array.isArray(data) ? data : [];
        return filterBedrockTags(list);
    } catch (error) {
        Logger.error('Error fetching grid tags:', error);
        return [];
    }
};

/**
 * Fetches recommended crossword puzzles after completing a puzzle.
 * @param {number} [limit=3] - Max number of recommendations
 * @returns {Promise<Array>} List of CrossRo (may be empty)
 */
const getCrossRecommend = async (limit = 3) => {
    try {
        const data = await ApiService.getData(`/home/cross-play/recommend?limit=${limit}`);
        return Array.isArray(data) ? data : [];
    } catch (error) {
        Logger.error('Error fetching cross recommendations:', error);
        return [];
    }
};

/**
 * Fetches recommended grid puzzles (word search / hidden message) after completing a puzzle.
 * @param {number} [limit=3] - Max number of recommendations
 * @returns {Promise<Array>} List of GridRo (may be empty)
 */
const getGridRecommend = async (limit = 3) => {
    try {
        const data = await ApiService.getData(`/home/grid-play/recommend?limit=${limit}`);
        return Array.isArray(data) ? data : [];
    } catch (error) {
        Logger.error('Error fetching grid recommendations:', error);
        return [];
    }
};

/** API accepts english | french | en | fr */
const normalizeWordOfDayLanguage = (language) => {
    const s = String(language || getContentLanguage() || 'english').toLowerCase().trim();
    if (s === 'en') return 'english';
    if (s === 'fr') return 'french';
    if (s === 'english' || s === 'french') return s;
    return 'english';
};

/**
 * GET /home/word-of-day/rating — logged-in 1–5 stars for that calendar day + language.
 */
const getWordOfTheDayRating = async ({ language, date } = {}) => {
    try {
        const lang = normalizeWordOfDayLanguage(language);
        const search = new URLSearchParams();
        search.set("language", lang);
        const d = date != null && String(date).trim() !== "" ? String(date).trim() : "";
        if (d) search.set("date", d);
        const data = await ApiService.getData(`/home/word-of-day/rating?${search.toString()}`);
        if (data && (data.code === "FAILURE" || data.code === "NO_DATA")) {
            return null;
        }
        return data && typeof data === "object" ? data : null;
    } catch (error) {
        Logger.error("Error fetching word of the day rating:", error);
        return null;
    }
};

const putWordOfTheDayRating = async ({ language, date, stars } = {}) => {
    const lang = normalizeWordOfDayLanguage(language);
    const body = { language: lang, stars };
    const d = date != null && String(date).trim() !== "" ? String(date).trim() : "";
    if (d) body.date = d;
    const data = await ApiService.putData("/home/word-of-day/rating", body);
    if (data && data.code === "FAILURE") {
        throw new Error(data.message || "Failed to save rating");
    }
    return data;
};

/**
 * GET /home/word-of-day — optional date yyyy-MM-dd (Montreal); omit for today.
 * @returns {Promise<{ kind: 'ok', rows: Array } | { kind: 'nodata' } | { kind: 'error' } | { kind: 'raw', data: any }>}
 */
const getWordOfTheDay = async ({ language, date } = {}) => {
    try {
        const lang = normalizeWordOfDayLanguage(language);
        const search = new URLSearchParams();
        search.set('language', lang);
        const d = date != null && String(date).trim() !== '' ? String(date).trim() : '';
        if (d) search.set('date', d);
        const data = await ApiService.getData(`/home/word-of-day?${search.toString()}`);
        if (data && data.code === 'NO_DATA') {
            return { kind: 'nodata' };
        }
        if (data && data.code === 'FAILURE') {
            return { kind: 'error' };
        }
        if (Array.isArray(data)) {
            return { kind: 'ok', rows: data };
        }
        if (data && Array.isArray(data.items)) {
            return {
                kind: 'ok',
                rows: data.items,
                responseDate: typeof data.date === 'string' ? data.date : undefined,
                isTodayFromApi: typeof data.isToday === 'boolean' ? data.isToday : undefined,
            };
        }
        return { kind: 'raw', data };
    } catch (error) {
        Logger.error('Error fetching word of the day:', error);
        return { kind: 'error' };
    }
};

/** Spring Page numeric fields sometimes arrive as strings over the wire. */
const parseFiniteNumber = (v) => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "") {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    }
    return null;
};

const normalizeHomeV2PagedResponse = (data) => {
    if (!data) {
        return { content: [], totalElements: 0, totalPages: 0, number: 0, size: 0, hasNext: false };
    }
    const content = Array.isArray(data.content) ? data.content : [];
    const pg = data.page && typeof data.page === "object" ? data.page : null;
    const num = parseFiniteNumber;

    let totalElements = num(data.totalElements) ?? num(data.total_elements);
    let totalPages = num(data.totalPages) ?? num(data.total_pages);
    let number = num(data.number) ?? num(data.page_number);
    let size = num(data.size) ?? num(data.pageSize);

    if (pg) {
        if (totalElements == null) totalElements = num(pg.totalElements) ?? num(pg.total_elements);
        if (totalPages == null) totalPages = num(pg.totalPages) ?? num(pg.total_pages);
        if (number == null) number = num(pg.number) ?? num(pg.page_number);
        if (size == null) size = num(pg.size) ?? num(pg.pageSize);
    }

    if (totalElements == null) totalElements = content.length;
    if (size == null || size <= 0) size = content.length > 0 ? content.length : 12;
    if (number == null) number = 0;
    if (totalPages == null && totalElements > 0 && size > 0) {
        totalPages = Math.max(1, Math.ceil(totalElements / size));
    }
    if (totalPages == null) totalPages = 0;

    const lastFlag =
        typeof data.last === "boolean"
            ? data.last
            : pg && typeof pg.last === "boolean"
              ? pg.last
              : typeof data.page?.last === "boolean"
                ? data.page.last
                : null;

    let hasNext = false;
    if (typeof lastFlag === "boolean") {
        hasNext = !lastFlag;
    } else if (totalPages > 0 && number != null) {
        hasNext = number < totalPages - 1;
    }

    return {
        content,
        totalElements,
        totalPages,
        number,
        size,
        hasNext,
    };
};

/** @see SlidingService.listBrowse */
const getSlidingPage = (params) => SlidingService.listBrowse(params);

/** @see SlidingService.getDistinctTags — `[{ tag, count }, …]` */
const getSlidingDistinctTags = async (language) => {
    const result = await SlidingService.getDistinctTags({
        language,
        state: "PUBLISHED",
    });
    return Array.isArray(result?.data) ? result.data : [];
};

/** @see SlidingService.getStoryGroup */
const getSlidingStoryGroup = (storyId) => SlidingService.getStoryGroup(storyId);

/** @see SlidingService.getFreeLatest */
const getHomeFreeLatest = (opts) => SlidingService.getFreeLatest(opts);

/** @see JigsawService.getFreeLatest */
const getHomeJigsawFreeLatest = (opts) => JigsawService.getFreeLatest(opts);

/** @see JigsawService.getFreeFeatured */
const getHomeJigsawFreeFeatured = (opts) => JigsawService.getFreeFeatured(opts);

/** @see JigsawService.listBrowse */
const getJigsawPage = (params) => JigsawService.listBrowse(params);

/** @see JigsawService.getDistinctTags — `[{ tag, count }, …]` */
const getJigsawDistinctTags = async (language) => {
    const result = await JigsawService.getDistinctTags({
        language,
        state: "PUBLISHED",
    });
    return Array.isArray(result?.data) ? result.data : [];
};

/** GET /home/story — page, pageSize, optional language, optional tags (repeat tags=). Sort: published_at desc (server). */
const getStoriesPage = async ({ page = 0, pageSize = 12, language, tags, keyword, puzzleType } = {}) => {
    try {
        const params = new URLSearchParams({
            page: String(page),
            pageSize: String(pageSize),
        });
        const lang = language ?? getContentLanguage();
        if (lang) params.set("language", lang);
        const kw = typeof keyword === "string" ? keyword.trim() : "";
        if (kw) params.set("keyword", kw);
        const type = typeof puzzleType === "string" ? puzzleType.trim() : "";
        if (type) params.set("puzzleType", type);
        const tagList = Array.isArray(tags) ? tags : [];
        tagList.forEach((t) => {
            if (t != null && String(t).trim() !== "") params.append("tags", String(t).trim());
        });
        const data = await ApiService.getData(`/home/story?${params.toString()}`);
        return normalizeHomeV2PagedResponse(data);
    } catch (error) {
        Logger.error("Error fetching stories page:", error);
        return { content: [], totalElements: 0, totalPages: 0, number: 0, size: 0, hasNext: false };
    }
};

/**
 * GET /home/story/distinct-tags — tag + count for published templates with story + topic.
 */
const getStoryDistinctTags = async (language) => {
    try {
        const lang = language ?? getContentLanguage();
        const qs = lang ? `?language=${encodeURIComponent(lang)}` : "";
        const data = await ApiService.getData(`/home/story/distinct-tags${qs}`);
        const list = Array.isArray(data) ? data : [];
        return filterBedrockTags(list);
    } catch (error) {
        Logger.error("Error fetching story distinct tags:", error);
        return [];
    }
};

/**
 * Single-story payloads from `/home/story/*` may be nested under `data`, `content`, or DTO keys after decrypt.
 */
function normalizeHomeV2StoryDetail(raw) {
    if (!raw || typeof raw !== "object") return raw;
    if (raw.code === "FAILURE") return null;

    const looksLikeStoryRow = (o) =>
        o &&
        typeof o === "object" &&
        !Array.isArray(o) &&
        (o.crossword != null ||
            o.grid != null ||
            o.crossTpl != null ||
            o.gridTpl != null ||
            o.kind != null ||
            o.puzzleType != null ||
            o.templateId != null ||
            (o.template != null && typeof o.template === "object"));

    if (looksLikeStoryRow(raw)) return raw;

    const nestedKeys = [
        "homeStoryRo",
        "homeStory",
        "storyRow",
        "row",
        "item",
        "payload",
        "body",
        "result",
    ];
    for (const key of nestedKeys) {
        const inner = raw[key];
        if (inner && typeof inner === "object" && inner !== raw) {
            const u = normalizeHomeV2StoryDetail(inner);
            if (u && looksLikeStoryRow(u)) return u;
        }
    }
    const content = raw.content;
    if (content && typeof content === "object" && !Array.isArray(content)) {
        const u = normalizeHomeV2StoryDetail(content);
        if (u && looksLikeStoryRow(u)) return u;
    }
    const data = raw.data;
    if (data && typeof data === "object" && !Array.isArray(data)) {
        const u = normalizeHomeV2StoryDetail(data);
        if (u && looksLikeStoryRow(u)) return u;
    }
    if (Array.isArray(raw.items) && raw.items.length === 1) {
        const u = normalizeHomeV2StoryDetail(raw.items[0]);
        if (u && looksLikeStoryRow(u)) return u;
    }
    return raw;
}

/** GET /home/story/{storyId} — same shape as list row (encrypted like other /home). */
const getStoryById = async (storyId) => {
    try {
        if (!storyId) return null;
        const raw = await ApiService.getData(`/home/story/${encodeURIComponent(storyId)}`);
        return normalizeHomeV2StoryDetail(raw);
    } catch (error) {
        Logger.error("Error fetching story by id:", error);
        return null;
    }
};

/** GET /home/story/story-of-the-day — featured story for `/puzzle-of-day` (optional `language`). */
const getStoryOfTheDay = async (language) => {
    try {
        const lang = language ?? getContentLanguage();
        const params = new URLSearchParams();
        if (lang) params.set("language", lang);
        const qs = params.toString() ? `?${params.toString()}` : "";
        const raw = await ApiService.getData(`/home/story/story-of-the-day${qs}`);
        return normalizeHomeV2StoryDetail(raw);
    } catch (error) {
        Logger.error("Error fetching story of the day:", error);
        return null;
    }
};

/** GET /home/story/carousel — landing story carousel (optional `language`). */
const getStoryCarousel = async (language) => {
    try {
        const lang = language ?? getContentLanguage();
        const params = new URLSearchParams();
        if (lang) params.set("language", lang);
        const qs = params.toString() ? `?${params.toString()}` : "";
        return await ApiService.getData(`/home/story/carousel${qs}`);
    } catch (error) {
        Logger.error("Error fetching story carousel:", error);
        return null;
    }
};

/** Append `free` filter when API supports tiered browse (`all` = omit). */
function appendHomePuzzleAccessFilter(params, accessFilter) {
    const af = String(accessFilter ?? "all").toLowerCase();
    if (af === "free") params.set("free", "true");
    else if (af === "paid") params.set("free", "false");
}

/**
 * GET /home/puzzle — published cross + grid templates with topic; paging/tags like /home/story.
 * Query filter uses `puzzleType` (CROSSWORD | WORD_SEARCH | HIDDEN_MESSAGE). Options may pass `puzzleType` or legacy `kind`.
 * Sends `excludeCompleted=true` by default so authenticated users omit puzzles already completed (server no-op if unsupported).
 * @param {'all'|'free'|'paid'} [accessFilter] — free = free-only; paid = standard/premium only; all = no filter
 * @param {boolean} [excludeCompleted=true]
 */
const getHomePuzzlePage = async ({
    page = 0,
    pageSize = 12,
    language,
    tags,
    kind,
    puzzleType,
    accessFilter = "all",
    excludeCompleted = true,
} = {}) => {
    try {
        const params = new URLSearchParams({
            page: String(page),
            pageSize: String(pageSize),
        });
        const lang = language ?? getContentLanguage();
        if (lang) params.set("language", lang);
        const tagList = Array.isArray(tags) ? tags : [];
        tagList.forEach((t) => {
            if (t != null && String(t).trim() !== "") params.append("tags", String(t).trim());
        });
        const filterKind = puzzleType ?? kind;
        const k = filterKind != null ? String(filterKind).trim().toUpperCase() : "";
        if (k === "CROSSWORD" || k === "WORD_SEARCH" || k === "HIDDEN_MESSAGE") {
            params.set("puzzleType", k);
        }
        appendHomePuzzleAccessFilter(params, accessFilter);
        if (excludeCompleted) {
            params.set("excludeCompleted", "true");
        }
        const data = await ApiService.getData(`/home/puzzle?${params.toString()}`);
        return normalizeHomeV2PagedResponse(data);
    } catch (error) {
        Logger.error("Error fetching home puzzle page:", error);
        return { content: [], totalElements: 0, totalPages: 0, number: 0, size: 0, hasNext: false };
    }
};

/**
 * GET /home/puzzle/distinct-tags — tags on published cross + grid templates with topic.
 * @param {'all'|'free'|'paid'} [accessFilter]
 */
const getHomePuzzleDistinctTags = async (language, kind, accessFilter = "all") => {
    try {
        const lang = language ?? getContentLanguage();
        const params = new URLSearchParams();
        if (lang) params.set("language", lang);
        params.set("state", "PUBLISHED");
        const k = kind != null ? String(kind).trim().toUpperCase() : "";
        if (k === "CROSSWORD" || k === "WORD_SEARCH" || k === "HIDDEN_MESSAGE") {
            params.set("puzzleType", k);
        }
        appendHomePuzzleAccessFilter(params, accessFilter);
        const qs = params.toString() ? `?${params.toString()}` : "";
        const data = await ApiService.getData(`/home/puzzle/distinct-tags${qs}`);
        const list = Array.isArray(data) ? data : [];
        return filterBedrockTags(list);
    } catch (error) {
        Logger.error("Error fetching home puzzle distinct tags:", error);
        return [];
    }
};

/**
 * GET `/home/stats/plays` — play aggregates (used by Library stats banner).
 * Expected: `countPlay`, `countFinished`, and `averageScore` (int 0–100).
 */
const getPlayStats = async () => {
    try {
        const response = await ApiService.getData("/home/stats/plays");
        Logger.log("Play stats response:", response);

        if (response && typeof response === "object") {
            if (response.code === "FAILURE" || response.error) {
                throw new Error(response.message || response.error || "Failed to fetch play statistics");
            }
            return response.data || response.content || response;
        }

        return response || { countPlay: 0, countFinished: 0 };
    } catch (error) {
        Logger.error("Error fetching play statistics:", error);
        throw error;
    }
};

/** GET /home/puzzle/{templateId} — HomePuzzleRo or empty; cross first then grid. */
const getHomePuzzleByTemplateId = async (templateId) => {
    try {
        if (!templateId) return null;
        return await ApiService.getData(`/home/puzzle/${encodeURIComponent(templateId)}`);
    } catch (error) {
        Logger.error("Error fetching home puzzle by template id:", error);
        return null;
    }
};

export { normalizeHomeV2PagedResponse };

const HomeService = {
    getHomePuzzles,
    getPlayStats,
    getCrossTags,
    getGridTags,
    getCrossRecommend,
    getGridRecommend,
    getWordOfTheDay,
    getWordOfTheDayRating,
    putWordOfTheDayRating,
    getSlidingPage,
    getSlidingDistinctTags,
    getSlidingStoryGroup,
    getHomeFreeLatest,
    getHomeJigsawFreeLatest,
    getHomeJigsawFreeFeatured,
    getJigsawPage,
    getJigsawDistinctTags,
    getStoriesPage,
    getStoryDistinctTags,
    getStoryById,
    getStoryOfTheDay,
    getStoryCarousel,
    getHomePuzzlePage,
    getHomePuzzleDistinctTags,
    getHomePuzzleByTemplateId,
    postAssistantHelp,
    getAssistantChatHistory,
};

export default HomeService;
export { getAssistantChatHistory, postAssistantHelp } from "./AssistantService";
